<template>
    <div class="layout-topbar">
        <div class="topbar-left">
            <a tabindex="0" class="menu-button" @click="toggleMenu">
                <i class="pi pi-chevron-left" />
            </a>
            <img class="horizontal-logo" src="/layout/images/logo-white.svg" alt="zeres" />
            <span class="topbar-separator" />
            <AppBreadcrumb />
            <RouterLink to="/"><img class="mobile-logo" :src="`/layout/images/logo-${isDarkTheme ? 'white' : 'dark'}.svg`" alt="zeres" /></RouterLink>
        </div>

        <div class="topbar-right">
            <ul class="topbar-menu">
                <!-- staging holds a copy of production's data; the tag and colour say which one this is -->
                <li v-if="tag" class="flex items-center">
                    <Tag :value="tag" :severity="tag === 'Staging' ? 'warn' : 'help'" class="text-base!" />
                </li>
                <AppCallFollow />
                <li class="right-sidebar-item">
                    <a class="right-sidebar-button cursor-pointer" title="Search customers (Ctrl+K)" @click="toggleSearchBar">
                        <i class="pi pi-search" />
                    </a>
                </li>
                <li class="right-sidebar-item">
                    <button type="button" class="app-config-button cursor-pointer" @click="toggleConfigSidebar" aria-label="Toggle Config Sidebar">
                        <i class="pi pi-cog" />
                    </button>
                </li>
                <li class="profile-item static sm:relative">
                    <a
                        class="bg-none! border-none! outline-none!"
                        v-styleclass="{ selector: '@next', enterFromClass: 'hidden', enterActiveClass: 'p-anchored-overlay-enter-active', leaveActiveClass: 'p-anchored-overlay-leave-active', leaveToClass: 'hidden', hideOnOutsideClick: true }"
                    >
                        <!-- the signed-in user's initials, as /me hands them out; blank until they arrive -->
                        <Avatar :label="initials" shape="square" class="w-10! h-10! rounded-lg! text-base! font-medium" aria-label="Signed-in user" />
                    </a>
                    <div
                        class="list-none p-2 m-0 rounded-2xl border border-surface overflow-hidden absolute bg-surface-0 dark:bg-surface-900 hidden origin-top w-52 mt-2 right-0 z-[999] top-auto shadow-[0px_56px_16px_0px_rgba(0,0,0,0.00),0px_36px_14px_0px_rgba(0,0,0,0.01),0px_20px_12px_0px_rgba(0,0,0,0.02),0px_9px_9px_0px_rgba(0,0,0,0.03),0px_2px_5px_0px_rgba(0,0,0,0.04)]"
                    >
                        <ul class="flex flex-col gap-1">
                            <li>
                                <a class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis cursor-pointer" @click="signOut">
                                    <i class="pi pi-power-off" />
                                    <span>Log out</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup>
import { signOut } from '@/auth/msal';
import { currentUserLoadError, loadCurrentUser, useCurrentUser } from '@/composables/useCurrentUser';
import { useFeedback } from '@/composables/useFeedback';
import { applyEnvironmentTheme, environmentTag } from '@/layout/composables/environmentTheme';
import { useLayout } from '@/layout/composables/layout';
import { computed, onMounted } from 'vue';
import AppBreadcrumb from './AppBreadcrumb.vue';
import AppCallFollow from './AppCallFollow.vue';

const { layoutConfig, layoutState, isDarkTheme, toggleMenu, toggleConfigSidebar, updateBodyBackground } = useLayout();
//resolved during setup: the toast behind it is injected, which an async callback cannot do
const feedback = useFeedback();

//the API derives the initials from the name claim, so every client shows the same ones
const { initials, failed, environment } = useCurrentUser();
const tag = computed(() => environmentTag(environment.value));

onMounted(async () => {
    await loadCurrentUser();
    const colour = applyEnvironmentTheme(environment.value);
    if (colour) {
        layoutConfig.primary = colour;
        updateBodyBackground(colour);
    }
    if (failed.value) feedback.error('Loading the signed-in user failed', currentUserLoadError());
});

function toggleSearchBar() {
    layoutState.searchBarActive = !layoutState.searchBarActive;
}
</script>
